/**
 * Модуль для загрузки, фильтрации и сортировки коллекции существ
 */

import { logger } from "../../utils/logger.js";

/**
 * Создаёт менеджер коллекции существ
 */
export function createCollectionScreenManager({
  safeFetch,
  buildAuthHeaders,
  getApiUrl,
  getTelegramWebApp,
  getCurrentUser,
  setCurrentUser,
  updateUserBalancesUI,
  fetchProfessionsData,
  renderCreatureCard,
  documentRef = document,
}) {
  let collection = [];
  let currentFilter = "all";
  let currentSort = "none";
  
  // Получаем утилиты из глобального контекста
  const getDomCache = () => window.__domCacheManager;
  const getLoadingManager = () => window.__loadingIndicatorManager;

  /**
   * Загружает коллекцию пользователя
   */
  async function loadCollectionScreen() {
    const domCache = getDomCache();
    const loadingManager = getLoadingManager();
    
    const container = domCache?.getCachedElement("#collection-items", documentRef) 
      || documentRef.getElementById("collection-items");
    
    if (!container) {
      logger.error("Collection items container not found");
      return;
    }

    // Показываем skeleton loader
    if (loadingManager) {
      loadingManager.showLoading("collection-load", {
        target: container,
        type: "skeleton"
      });
    } else {
      // Fallback на старый способ
      domCache?.updateHTML("#collection-items", '<div class="loading">Загрузка...</div>', documentRef) ||
      (container.innerHTML = '<div class="loading">Загрузка...</div>');
    }

    const telegramWebApp = getTelegramWebApp();
    if (!telegramWebApp || !telegramWebApp.initData) {
      domCache?.updateHTML("#collection-items", '<div class="loading">Ожидание авторизации...</div>', documentRef) ||
      (container.innerHTML = '<div class="loading">Ожидание авторизации...</div>');
      
      setTimeout(() => {
        const app = getTelegramWebApp();
        if (app && app.initData) {
          loadCollectionScreen();
        }
      }, 1000);
      return;
    }

    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/user/me`, {
        headers: buildAuthHeaders(),
        showLoading: false, // Управляем загрузкой вручную
      });

      if (response.success && response.data.success) {
        collection = response.data.collection || [];
        const user = response.data.user;
        setCurrentUser(user);
        updateUserBalancesUI(user);

        try {
          await fetchProfessionsData();
        } catch (error) {
          logger.warn(
            "Не удалось обновить список профессий:",
            error?.message || error,
          );
        }

        renderCollection();
      } else {
        const errorHTML = '<div class="error">Ошибка загрузки коллекции</div>';
        domCache?.updateHTML("#collection-items", errorHTML, documentRef) ||
        (container.innerHTML = errorHTML);
      }
    } catch (error) {
      logger.error("Load collection error:", error);
      const errorHTML = '<div class="error">Ошибка загрузки коллекции</div>';
      domCache?.updateHTML("#collection-items", errorHTML, documentRef) ||
      (container.innerHTML = errorHTML);
    } finally {
      // Скрываем skeleton loader
      if (loadingManager) {
        loadingManager.hideLoading("collection-load");
      }
    }
  }

  /**
   * Рендерит коллекцию с применением фильтров и сортировки
   */
  function renderCollection() {
    const domCache = getDomCache();
    const container = domCache?.getCachedElement("#collection-items", documentRef) 
      || documentRef.getElementById("collection-items");
    
    if (!container) {
      return;
    }

    if (collection.length === 0) {
      const emptyHTML = '<div class="loading">Коллекция пуста</div>';
      domCache?.updateHTML("#collection-items", emptyHTML, documentRef) ||
      (container.innerHTML = emptyHTML);
      return;
    }

    // Применяем фильтр
    let filtered = collection;
    if (currentFilter !== "all") {
      filtered = collection.filter(
        (item) => (item.rarity || "").toLowerCase() === currentFilter.toLowerCase(),
      );
    }

    // Применяем сортировку
    let sorted = [...filtered];
    switch (currentSort) {
      case "level":
        sorted.sort((a, b) => (b.level || 0) - (a.level || 0));
        break;
      case "name":
        sorted.sort((a, b) => {
          const nameA = (a.name || a.creature_name || "").toLowerCase();
          const nameB = (b.name || b.creature_name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
      case "rarity":
        const rarityOrder = { legendary: 4, epic: 3, rare: 2, common: 1 };
        sorted.sort((a, b) => {
          const rarityA = rarityOrder[(a.rarity || "").toLowerCase()] || 0;
          const rarityB = rarityOrder[(b.rarity || "").toLowerCase()] || 0;
          return rarityB - rarityA;
        });
        break;
    }

    // Очищаем контейнер и добавляем карточки
    container.innerHTML = "";

    if (sorted.length === 0) {
      const noResultsHTML = '<div class="loading">Нет существ с выбранным фильтром</div>';
      domCache?.updateHTML("#collection-items", noResultsHTML, documentRef) ||
      (container.innerHTML = noResultsHTML);
      return;
    }

    // Используем DocumentFragment для батчинга DOM операций
    const fragment = documentRef.createDocumentFragment();
    sorted.forEach((item) => {
      const card = renderCreatureCard(item, false);
      if (card) {
        fragment.appendChild(card);
      }
    });
    container.appendChild(fragment);
  }

  /**
   * Устанавливает фильтр коллекции
   */
  function filterCollection(filter) {
    currentFilter = filter;
    const domCache = getDomCache();
    
    // Обновляем активные классы фильтров
    documentRef.querySelectorAll('[id^="filter-"]').forEach((el) => {
      el.classList.remove("active");
    });
    
    const filterEl = domCache?.getCachedElement(`#filter-${filter}`, documentRef) 
      || documentRef.getElementById(`filter-${filter}`);
    if (filterEl) {
      filterEl.classList.add("active");
    }
    renderCollection();
  }

  /**
   * Устанавливает сортировку коллекции
   */
  function sortCollection(sort) {
    currentSort = currentSort === sort ? "none" : sort;
    const domCache = getDomCache();
    
    // Обновляем активные классы сортировки
    documentRef.querySelectorAll('[id^="sort-"]').forEach((el) => {
      el.classList.remove("active");
    });
    
    const sortEl = domCache?.getCachedElement(`#sort-${sort}`, documentRef) 
      || documentRef.getElementById(`sort-${sort}`);
    if (sortEl && currentSort !== "none") {
      sortEl.classList.add("active");
    }
    renderCollection();
  }

  /**
   * Получает текущую коллекцию
   */
  function getCollection() {
    return collection;
  }

  return {
    loadCollectionScreen,
    filterCollection,
    sortCollection,
    renderCollection,
    getCollection,
  };
}










